import { useEffect, useRef, useState, type RefObject } from 'react'
import { usePrefersReducedMotion } from './useInView'

/** True only on devices with a real hover-capable pointer. */
function useFinePointer(): boolean {
  const [fine, setFine] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)')
    setFine(mq.matches)
    const onChange = (event: MediaQueryListEvent) => setFine(event.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  return fine
}

/**
 * Tilts a card in 3D toward the cursor and tracks a soft spotlight behind it.
 * The spotlight position is published as CSS custom properties so the visual
 * treatment stays in the stylesheet.
 */
export function useTilt<T extends HTMLElement>(maxDegrees = 6) {
  const ref = useRef<T>(null)
  const reduced = usePrefersReducedMotion()
  const fine = useFinePointer()

  useEffect(() => {
    const node = ref.current
    if (!node || reduced || !fine) return

    let frame = 0

    const onMove = (event: PointerEvent) => {
      const rect = node.getBoundingClientRect()
      const px = (event.clientX - rect.left) / rect.width
      const py = (event.clientY - rect.top) / rect.height

      if (frame) cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        node.style.setProperty('--spot-x', `${px * 100}%`)
        node.style.setProperty('--spot-y', `${py * 100}%`)
        node.style.transform =
          `perspective(1100px) rotateX(${(0.5 - py) * maxDegrees * 2}deg) ` +
          `rotateY(${(px - 0.5) * maxDegrees * 2}deg) translate3d(0,0,0)`
      })
    }

    const onLeave = () => {
      if (frame) cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        node.style.transform = ''
        node.style.removeProperty('--spot-x')
        node.style.removeProperty('--spot-y')
      })
    }

    node.addEventListener('pointermove', onMove)
    node.addEventListener('pointerleave', onLeave)

    return () => {
      node.removeEventListener('pointermove', onMove)
      node.removeEventListener('pointerleave', onLeave)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [fine, maxDegrees, reduced])

  return ref
}

/**
 * Translates an element as it passes through the viewport.
 * `speed` is a fraction of the scrolled distance; negative moves against it.
 */
export function useParallax<T extends HTMLElement>(speed = 0.12): RefObject<T> {
  const ref = useRef<T>(null)
  const reduced = usePrefersReducedMotion()

  useEffect(() => {
    const node = ref.current
    if (!node || reduced) return

    let frame = 0

    const update = () => {
      frame = 0
      const rect = node.getBoundingClientRect()
      // 0 when the element is centred; ± as it moves away.
      const offset = rect.top + rect.height / 2 - window.innerHeight / 2
      node.style.transform = `translate3d(0, ${-offset * speed}px, 0)`
    }

    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(update)
    }

    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      if (frame) cancelAnimationFrame(frame)
      node.style.transform = ''
    }
  }, [reduced, speed])

  return ref
}

/** Reports whether the page is scrolling up or down, plus how far down it is. */
export function useScrollDirection() {
  const [state, setState] = useState<{ direction: 'up' | 'down'; atTop: boolean }>({
    direction: 'up',
    atTop: true,
  })

  useEffect(() => {
    let last = window.scrollY
    let frame = 0

    const update = () => {
      frame = 0
      const y = window.scrollY
      // Ignore sub-pixel jitter and rubber-banding past the top.
      if (Math.abs(y - last) > 6) {
        setState({ direction: y > last && y > 120 ? 'down' : 'up', atTop: y < 12 })
        last = y
      } else {
        setState((current) => (current.atTop === y < 12 ? current : { ...current, atTop: y < 12 }))
      }
    }

    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(update)
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [])

  return state
}
